import React from 'react';
import { 
  Book, // Books
  Shirt, // Apparel
  FileText, // Stationery
  Package, // Packaging
  Monitor, // Digital
  MapPin, // Outdoor
  Gift, // Merchandise
  Newspaper, // Publishing
  Coffee, // Food & Beverage
  Home, // Home & Office
  Calendar, // Events
  BookOpen,
  Package2,
  Laptop,
  Map,
  ShoppingBag,
  Megaphone,
  UtensilsCrossed,
  Briefcase,
  PartyPopper,
  Palette
} from 'lucide-react';

// Template category icons
export const CategoryIcons: Record<string, React.FC<{ className?: string }>> = {
  // Main categories
  books: Book,
  apparel: Shirt,
  stationery: FileText,
  packaging: Package,
  digital: Monitor,
  outdoor: MapPin,
  merchandise: Gift,
  publishing: Newspaper,
  'food-beverage': Coffee,
  'home-office': Home,
  events: Calendar,
  
  // Legacy categories (for backward compatibility)
  software: Package2,
  stands: Megaphone,
  branding: Palette,
  marketing: Megaphone
};

// Category styles with colors
export const CategoryStyles: Record<string, {
  color: string;
  bgColor: string;
  borderColor: string;
  gradientFrom: string;
  gradientTo: string;
}> = {
  books: {
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    gradientFrom: 'from-indigo-500',
    gradientTo: 'to-indigo-600'
  },
  apparel: {
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    gradientFrom: 'from-emerald-500',
    gradientTo: 'to-emerald-600'
  },
  stationery: {
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    gradientFrom: 'from-blue-500',
    gradientTo: 'to-blue-600'
  },
  packaging: {
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    gradientFrom: 'from-purple-500',
    gradientTo: 'to-purple-600'
  },
  digital: {
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    gradientFrom: 'from-cyan-500',
    gradientTo: 'to-cyan-600'
  },
  outdoor: {
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    gradientFrom: 'from-orange-500',
    gradientTo: 'to-orange-600'
  },
  merchandise: {
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    gradientFrom: 'from-red-500',
    gradientTo: 'to-red-600'
  },
  publishing: {
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200',
    gradientFrom: 'from-slate-500',
    gradientTo: 'to-slate-600'
  },
  'food-beverage': {
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    gradientFrom: 'from-amber-500',
    gradientTo: 'to-amber-600'
  },
  'home-office': {
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-200',
    gradientFrom: 'from-teal-500',
    gradientTo: 'to-teal-600'
  },
  events: {
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    borderColor: 'border-pink-200',
    gradientFrom: 'from-pink-500',
    gradientTo: 'to-pink-600'
  },
  
  // Legacy category styles
  software: {
    color: 'text-violet-600',
    bgColor: 'bg-violet-50',
    borderColor: 'border-violet-200',
    gradientFrom: 'from-violet-500',
    gradientTo: 'to-violet-600'
  },
  stands: {
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    gradientFrom: 'from-orange-500',
    gradientTo: 'to-orange-600'
  },
  branding: {
    color: 'text-rose-600',
    bgColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    gradientFrom: 'from-rose-500',
    gradientTo: 'to-rose-600'
  },
  marketing: {
    color: 'text-fuchsia-600',
    bgColor: 'bg-fuchsia-50',
    borderColor: 'border-fuchsia-200',
    gradientFrom: 'from-fuchsia-500',
    gradientTo: 'to-fuchsia-600'
  }
};

// Get icon component for a category
export function getCategoryIcon(category: string): React.FC<{ className?: string }> {
  return CategoryIcons[category] || CategoryIcons.books;
}

// Get style for a category
export function getCategoryStyle(category: string) {
  return CategoryStyles[category] || CategoryStyles.books;
}

// Category display names
export const CategoryDisplayNames: Record<string, string> = {
  'books': 'Books',
  'apparel': 'Apparel & Wearables',
  'stationery': 'Print & Stationery',
  'packaging': 'Packaging & Products',
  'digital': 'Digital Screens',
  'outdoor': 'Outdoor & Signage',
  'merchandise': 'Promotional Items',
  'publishing': 'Publishing & Media',
  'food-beverage': 'Food & Beverage',
  'home-office': 'Home & Office',
  'events': 'Events & Exhibitions',
  
  // Legacy names
  'software': 'Software',
  'stands': 'Display Stands',
  'branding': 'Branding',
  'marketing': 'Marketing'
};
